import { create } from 'zustand';
import { createJSONStorage, persist } from 'zustand/middleware';

import type { ProductFilterValues } from '~/features/catalog-operations/types/catalog-operations.types';

interface ProductsPagination {
  /** 1-based, matches `useCurationProducts`'s `page` param. */
  page: number;
  pageSize: number;
}

interface ProductsListingState {
  /** Which batch this state currently belongs to — `null` before the first
   *  `ensureBatch` call. Used to detect a switch to a different batch's
   *  products page, since this store is a module-level singleton shared
   *  across every batch's instance of the page. */
  batchId: string | null;
  search: string;
  /** Drawer-applied filters only — excludes the implicit `needs_attention`
   *  tab flag, which stays derived in the component from the (URL-backed,
   *  unstored) active tab. */
  filters: ProductFilterValues;
  viewMode: 'card' | 'table';
  pagination: ProductsPagination;
  /** `null` = unsorted/default order (backend defaults to created_at). */
  sortBy: string | null;
  sortDir: 'asc' | 'desc';
}

interface ProductsListingActions {
  /** Resets every field to defaults (including `viewMode`) when switching to
   *  a different batch's products page; no-op when `batchId` already matches.
   *  Call synchronously at the top of ProductsPage's render, before reading
   *  any other selector, so a batch switch never flashes stale-batch data. */
  ensureBatch: (batchId: string) => void;
  /** Commits a search term. Resets pagination to page 1. */
  setSearch: (search: string) => void;
  /** Commits the filter-drawer payload. Resets pagination to page 1. */
  applyFilters: (filters: ProductFilterValues) => void;
  /** Switches card/table view and its associated page size. Resets pagination to page 1. */
  setViewMode: (viewMode: 'card' | 'table', pageSize: number) => void;
  /** Updates DataTable/FullPagination-controlled pagination. */
  setPagination: (pagination: ProductsPagination) => void;
  /** Commits the active sort column/direction. Does NOT reset the page —
   *  sorting the current result set doesn't change how many results there
   *  are, so there's no need to jump back to page 1. */
  setSort: (sortBy: string | null, sortDir: 'asc' | 'desc') => void;
  /** Resets only the page — used when switching tabs (tab itself lives in
   *  the URL, not this store, since it already round-trips correctly there). */
  resetPage: () => void;
  /** Restores full initial state. */
  reset: () => void;
}

const INITIAL_STATE: ProductsListingState = {
  batchId: null,
  search: '',
  filters: {},
  viewMode: 'table',
  pagination: { page: 1, pageSize: 10 },
  sortBy: null,
  sortDir: 'asc',
};

/** Store for the per-batch products listing page's search/filter/view-mode/
 *  pagination/sort state — survives SPA navigation to a product's detail page
 *  and back. Scoped per batch via `ensureBatch` since, unlike the singleton
 *  catalog-operations/experiments listings, this page is a template instantiated
 *  once per `batchId`.
 *
 *  Persisted to **sessionStorage**: the product detail page's sibling pager reads
 *  this state to locate the open product within the listing, and in-memory state
 *  would leave it with nothing after a refresh or a direct hit on the detail URL.
 *  sessionStorage keeps that per tab, so a stale filter set can't outlive the
 *  browsing session the way localStorage would. */
export const useProductsListingStore = create<ProductsListingState & ProductsListingActions>()(
  persist(
    (set, get) => ({
      ...INITIAL_STATE,

      ensureBatch: (batchId) => {
        if (get().batchId === batchId) return;
        set({ ...INITIAL_STATE, batchId });
      },

      setSearch: (search) => {
        set((s) => ({ search, pagination: { ...s.pagination, page: 1 } }));
      },

      applyFilters: (filters) => {
        set((s) => ({ filters, pagination: { ...s.pagination, page: 1 } }));
      },

      setViewMode: (viewMode, pageSize) => {
        set({ viewMode, pagination: { page: 1, pageSize } });
      },

      setPagination: (pagination) => {
        set({ pagination });
      },

      setSort: (sortBy, sortDir) => {
        set({ sortBy, sortDir });
      },

      resetPage: () => {
        set((s) => ({ pagination: { ...s.pagination, page: 1 } }));
      },

      reset: () => {
        set(INITIAL_STATE);
      },
    }),
    {
      name: 'products-listing-store',
      version: 1,
      storage: createJSONStorage(() => sessionStorage),
      // State only — the actions are recreated by the initializer on every load.
      partialize: (s) => ({
        batchId: s.batchId,
        search: s.search,
        filters: s.filters,
        viewMode: s.viewMode,
        pagination: s.pagination,
        sortBy: s.sortBy,
        sortDir: s.sortDir,
      }),
    },
  ),
);
